import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TelehealthDisplayMode } from '@models';
import {
  ActiveTelehealthCallService,
  TelehealthCallEngineService,
} from '@services';

@Component({
  selector: 'icaredx-telehealth-live-control',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  templateUrl: './telehealth-live-control.component.html',
})
export class TelehealthLiveControlComponent {
  private readonly engine = inject(TelehealthCallEngineService);
  private readonly call = inject(ActiveTelehealthCallService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly patientName = input<string | null>(null);
  readonly mode = input.required<TelehealthDisplayMode>();
  readonly unreadCount = input(0);

  readonly variant = input<'menu' | 'link'>('menu');

  readonly modeChange = output<TelehealthDisplayMode>();
  readonly activate = output<void>();
  readonly goToPatient = output<void>();

  protected readonly menuOpen = signal(false);
  private readonly nowMs = signal(0);

  private readonly remainingMs = computed(() => {
    const endsAt = this.call.activeAppointment()?.endUtc;
    if (!endsAt || this.engine.state() !== 'connected') {
      return null;
    }
    return new Date(endsAt).getTime() - this.nowMs();
  });

  protected readonly remainingLabel = computed(() => {
    const remaining = this.remainingMs();
    if (remaining === null || remaining <= 0 || remaining > 5 * 60 * 1000) {
      return null;
    }
    return `${this.clock(remaining)} left`;
  });

  protected readonly elapsedLabel = computed(() => {
    const start = this.engine.connectedAtMs();
    if (start === null) {
      return '';
    }
    return `${this.clock(Math.max(0, this.nowMs() - start))} elapsed`;
  });

  protected readonly timerLabel = computed(
    () => this.remainingLabel() ?? this.elapsedLabel()
  );

  protected readonly timerTextClass = computed(() =>
    this.remainingLabel() ? 'text-amber-600' : 'text-gray-600'
  );

  protected readonly pillBorderClass = computed(() =>
    this.remainingLabel() ? 'border-amber-400' : 'border-gray-200'
  );

  constructor() {
    if (this.isBrowser) {
      this.nowMs.set(Date.now());
      const intervalId = setInterval(() => this.nowMs.set(Date.now()), 1000);
      inject(DestroyRef).onDestroy(() => clearInterval(intervalId));
    }
  }

  protected onPillClick(): void {
    if (this.variant() === 'link') {
      this.activate.emit();
      return;
    }
    this.menuOpen.update(open => !open);
  }

  protected select(mode: TelehealthDisplayMode): void {
    this.modeChange.emit(mode);
    this.menuOpen.set(false);
  }

  protected onGoToPatient(): void {
    this.goToPatient.emit();
    this.menuOpen.set(false);
  }

  private clock(ms: number): string {
    const total = Math.floor(ms / 1000);
    const minutes = Math.floor(total / 60);
    const seconds = (total % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }
}
